import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

export default function ModuleAccessTab({ moduleKey, onAccessChanged }) {
  const { isSuperAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [configurators, setConfigurators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [addType, setAddType] = useState('team');
  const [addTarget, setAddTarget] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [accessRules, teamList, positionList, userList] = await Promise.all([
        api.getModuleAccess(moduleKey),
        api.getTeams().catch(() => []),
        api.getPositions().catch(() => []),
        api.getActiveUsers().catch(() => []),
      ]);
      setRules(accessRules || []);
      setTeams(teamList || []);
      setPositions(positionList || []);
      setUsers(Array.isArray(userList) ? userList : []);

      if (isSuperAdmin) {
        const configs = await api.getModuleConfigurators(moduleKey);
        setConfigurators(configs || []);
      }
    } catch (err) {
      console.error('Failed to load access data:', err);
    } finally {
      setLoading(false);
    }
  }, [moduleKey, isSuperAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // Team access is the primary model — check if a team has access
  function hasTeamAccess(teamName) {
    return rules.some(r => r.access_type === 'team' && r.access_target === teamName);
  }

  function toggleTeamAccess(teamName) {
    if (hasTeamAccess(teamName)) {
      setRules(rules.filter(r => !(r.access_type === 'team' && r.access_target === teamName)));
    } else {
      setRules([...rules, { access_type: 'team', access_target: teamName }]);
    }
  }

  function removeRule(idx) {
    setRules(rules.filter((_, i) => i !== idx));
  }

  function addRule() {
    if (!addTarget) return;
    const exists = rules.some(r => r.access_type === addType && r.access_target === String(addTarget));
    if (exists) return;
    setRules([...rules, { access_type: addType, access_target: String(addTarget) }]);
    setAddTarget('');
  }

  async function saveAccess() {
    setSaving(true);
    try {
      const updated = await api.updateModuleAccess(moduleKey, rules);
      setRules(updated || rules);
      if (typeof onAccessChanged === 'function') onAccessChanged();
    } catch (err) {
      alert('Failed to save access rules');
    } finally {
      setSaving(false);
    }
  }

  // Configurator management
  const [configUserIds, setConfigUserIds] = useState([]);
  useEffect(() => {
    setConfigUserIds(configurators.map(c => c.user_id));
  }, [configurators]);

  function toggleConfigurator(userId) {
    setConfigUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  }

  async function saveConfigurators() {
    setSaving(true);
    try {
      const updated = await api.updateModuleConfigurators(moduleKey, configUserIds);
      setConfigurators(updated || []);
    } catch (err) {
      alert('Failed to save configurators');
    } finally {
      setSaving(false);
    }
  }

  function getTargetLabel(type, target) {
    if (type === 'team') return teams.find(t => String(t.id) === target || t.name === target)?.name || target;
    if (type === 'role') return target;
    if (type === 'tier') return target;
    if (type === 'position') return positions.find(p => String(p.id) === target)?.name || target;
    if (type === 'user') {
      const u = users.find(u => String(u.id) === target);
      return u ? u.full_name : target;
    }
    return target;
  }

  function getSelectOptions() {
    if (addType === 'team') return teams.filter(t => t.is_active !== 0).map(t => ({ value: t.name, label: t.name }));
    if (addType === 'position') return positions.filter(p => p.is_active !== 0).map(p => ({ value: String(p.id), label: `${p.name} (${p.team_name || ''})` }));
    if (addType === 'user') return users.map(u => ({ value: String(u.id), label: u.full_name }));
    return [];
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', padding: 24 }}>Loading access settings...</p>;

  // Separate team rules from other rules for display
  const otherRules = rules.filter(r => r.access_type !== 'team');

  return (
    <div>
      {/* Team Access — primary model */}
      <div className="table-wrapper" style={{ marginBottom: 24 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Team Access</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            Super Admin always has access. Select which teams can see this module.
          </p>
        </div>
        <table className="data-table" style={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Team</th>
              <th style={{ textAlign: 'left' }}>Description</th>
              <th style={{ textAlign: 'center', width: 100 }}>Access</th>
            </tr>
          </thead>
          <tbody>
            {teams.filter(t => t.is_active !== 0).map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t.name}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t.description || '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={hasTeamAccess(t.name)}
                    onChange={() => toggleTeamAccess(t.name)}
                    style={{ cursor: 'pointer', width: 18, height: 18, accentColor: 'var(--brand-gold)' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Additional Access Rules */}
      <div className="table-wrapper" style={{ marginBottom: 24 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Additional Access</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            Grant access to specific positions or individual users beyond team membership.
          </p>
        </div>

        {otherRules.length > 0 && (
          <table className="data-table" style={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: 100 }}>Type</th>
                <th style={{ textAlign: 'left' }}>Target</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {otherRules.map((rule, idx) => {
                const realIdx = rules.indexOf(rule);
                return (
                  <tr key={idx}>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                        background: 'var(--bg-subtle)', color: 'var(--text-muted)', textTransform: 'capitalize',
                      }}>{rule.access_type}</span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{getTargetLabel(rule.access_type, rule.access_target)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-muted btn-sm" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => removeRule(realIdx)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ padding: '12px 18px', borderTop: otherRules.length > 0 ? '1px solid var(--border-subtle)' : 'none', display: 'flex', gap: 8, alignItems: 'center', background: 'var(--bg-subtle)' }}>
          <select className="form-input" style={{ width: 120, padding: '8px 12px', fontSize: 13 }} value={addType} onChange={e => { setAddType(e.target.value); setAddTarget(''); }}>
            <option value="team">Team</option>
            <option value="position">Position</option>
            <option value="user">User</option>
          </select>
          <select className="form-input" style={{ flex: 1, maxWidth: 280, padding: '8px 12px', fontSize: 13 }} value={addTarget} onChange={e => setAddTarget(e.target.value)}>
            <option value="">Select...</option>
            {getSelectOptions().map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={addRule} disabled={!addTarget}>Add</button>
        </div>
      </div>

      <button className="btn btn-gold" onClick={saveAccess} disabled={saving}>
        {saving ? 'Saving...' : 'Save Access Rules'}
      </button>

      {/* Module Configurators — super_admin only */}
      {isSuperAdmin && (
        <div className="table-wrapper" style={{ marginTop: 32 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Module Configurators</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Select users who can access this module's setup (in addition to Super Admin).
            </p>
          </div>
          <div style={{ padding: 18, maxHeight: 300, overflowY: 'auto' }}>
            {users.map(u => (
              <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={configUserIds.includes(u.id)}
                  onChange={() => toggleConfigurator(u.id)}
                  style={{ width: 16, height: 16, accentColor: 'var(--brand-gold)' }}
                />
                <span style={{ fontWeight: 500 }}>{u.full_name}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({u.role})</span>
              </label>
            ))}
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-subtle)' }}>
            <button className="btn btn-gold btn-sm" onClick={saveConfigurators} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configurators'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
